import React, { useState, useRef, useEffect } from 'react';
import './style.css';
import { ChevronDown } from 'lucide-react';

const DatapointInput = ({ datapoint, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    console.log('datapoint:', datapoint);
    // Handle clicking outside of dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Helper function to format selected values
    const formatSelectedValues = (values) => {
        const currentValues = values?.datapoint_value_updated !== null
            ? values.datapoint_value_updated
            : values.datapoint_value;

        if (!Array.isArray(currentValues) || currentValues.length === 0) return 'Select options...';

        const displayText = currentValues.join(', ');
        return displayText.length > 50
            ? displayText.substring(0, 47) + '...'
            : displayText;
    };

    const toTitleCase = (str) => {
        if (!str) return str;
        return str
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    const renderInput = () => {
        switch (datapoint.data_type) {
            case 'bool':
                const boolValue = datapoint.datapoint_value_updated !== undefined
                    ? datapoint.datapoint_value_updated
                    : datapoint.datapoint_value;

                const getDisplayValue = (value) => {
                    if (value === true) return 'Yes';
                    if (value === false) return 'No';
                    return 'Select...';
                };

                return (
                    <div className="select-container" ref={dropdownRef}>
                        <button
                            className="select-button"
                            onClick={() => setIsOpen(!isOpen)}
                            type="button"
                        >
                            <span className="select-text">
                                {getDisplayValue(boolValue)}
                            </span>
                            <ChevronDown
                                className={`select-chevron ${isOpen ? 'open' : ''}`}
                                size={20}
                            />
                        </button>
                        {isOpen && (
                            <div className="select-dropdown">
                                <button
                                    className="select-option"
                                    onClick={() => {
                                        onChange(true);
                                        setIsOpen(false);
                                    }}
                                >
                                    Yes
                                </button>
                                <button
                                    className="select-option"
                                    onClick={() => {
                                        onChange(false);
                                        setIsOpen(false);
                                    }}
                                >
                                    No
                                </button>
                            </div>
                        )}
                    </div>
                );

            case 'num':

                // Split num_type into [numberType, displayType]
                const [numberType, displayType] = datapoint.num_type?.split('_') || ['', ''];
                const isInteger = numberType === 'int';
                const isFloat = numberType === 'float';
                const isPercentage = displayType === 'pct';
                const isDollar = displayType === 'dollar';

                // Format the display value based on num_type
                const formatValue = (val) => {
                    if (val === null || val === '') return '';
                    const num = Number(val);
                    if (isNaN(num)) return '';

                    if (isInteger) {
                        // Add comma formatting for integers
                        return Math.ceil(num).toLocaleString('en-US');
                    } else if (isFloat) {
                        if (isPercentage) {
                            // Convert decimal to percentage (0.14 -> 14.00)
                            return (num * 100).toFixed(2);
                        }
                        // For non-percentage floats
                        return num.toFixed(4);
                    }
                    return num;
                };

                const displayValue = formatValue(
                    datapoint.datapoint_value_updated !== null
                        ? datapoint.datapoint_value_updated
                        : datapoint.datapoint_value
                );
                console.log(`Datapoint: ${datapoint.headline} (${numberType}_${displayType}), Formated Value: ${displayValue}`);

                // Set display symbols based on type
                let pre_value_symbol = null;
                let post_value_symbol = null;
                // let pre_value_symbol = '';
                // let post_value_symbol = '';
                if (isDollar) {
                    pre_value_symbol = '$';
                } else if (isPercentage) {
                    post_value_symbol = '%';
                }

                return (
                    <div class={`input-icon ${post_value_symbol ? 'input-icon-right' : 'input-icon-left'}`}>
                        {pre_value_symbol && <i>{pre_value_symbol}</i>}
                        <input
                            type="text"
                            value={`${displayValue}`}
                            // value={`${pre_value_symbol}${displayValue}${post_value_symbol}`}
                            onChange={(e) => {
                                const rawValue = e.target.value
                                    .replace('$', '')
                                    .replace('%', '')
                                    .replace(/,/g, ''); // Remove all commas
                                if (rawValue === '') {
                                    onChange('');
                                } else {
                                    const numValue = Number(rawValue);
                                    if (!isNaN(numValue)) {
                                        // Convert percentage back to decimal if needed
                                        const finalValue = isPercentage ? numValue / 100 : numValue;
                                        onChange(finalValue);
                                    }
                                }
                            }}
                            className="datapoint-input"
                        />
                        {post_value_symbol && <i>{post_value_symbol}</i>}
                    </div>
                );

            case 'list':
                const listValue = datapoint.datapoint_value_updated !== null
                    ? datapoint.datapoint_value_updated
                    : datapoint.datapoint_value || '';
                
                const formattedListValue = toTitleCase(listValue);
                console.log('listValue:', formattedListValue);
                return (
                    <div className="select-container" ref={dropdownRef}>
                        <button
                            className="select-button"
                            onClick={() => setIsOpen(!isOpen)}
                            type="button"
                        >
                            <span className="select-text">
                                {formattedListValue || 'Select...'}
                            </span>
                            <ChevronDown
                                className={`select-chevron ${isOpen ? 'open' : ''}`}
                                size={20}
                            />
                        </button>
                        {isOpen && (
                            <div className="select-dropdown">
                                {datapoint.options.map(option => (
                                    <button
                                        key={option}
                                        className="select-option"
                                        onClick={() => {
                                            onChange(option);
                                            setIsOpen(false);
                                        }}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 'multi_list':
                const currentValue = datapoint.datapoint_value_updated !== null
                    ? datapoint.datapoint_value_updated
                    : datapoint.datapoint_value;

                return (
                    <div className="select-container" ref={dropdownRef}>
                        <button
                            className="select-button"
                            onClick={() => setIsOpen(!isOpen)}
                            type="button"
                        >
                            <span className="select-text">
                                {formatSelectedValues(datapoint)}
                            </span>
                            <ChevronDown
                                className={`select-chevron ${isOpen ? 'open' : ''}`}
                                size={20}
                            />
                        </button>
                        {isOpen && (
                            <div className="select-dropdown">
                                {datapoint.options.map(option => (
                                    <label key={option} className="multi-select-option">
                                        <input
                                            type="checkbox"
                                            checked={Array.isArray(currentValue) &&
                                                currentValue.includes(option)}
                                            onChange={(e) => {
                                                const values = Array.isArray(currentValue)
                                                    ? currentValue
                                                    : [];
                                                const newValues = e.target.checked
                                                    ? [...values, option]
                                                    : values.filter(v => v !== option);
                                                onChange(newValues);
                                            }}
                                        />
                                        {option}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="update-data-datapoint-container">
            <label className="datapoint-label text-align-center">{datapoint.headline}</label>
            {renderInput()}
        </div>
    );
};

export default DatapointInput;
